/* eslint-disable react-refresh/only-export-components -- buttonVariants is part of the public API */
import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  // Base classes: whitespace-nowrap prevents text overflow; flex-shrink-0 keeps buttons from squishing in flex containers
  'inline-flex items-center justify-center gap-2 rounded-md font-medium whitespace-nowrap flex-shrink-0 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-primary text-white hover:bg-primary/90 focus-visible:ring-primary',
        primary: 'bg-primary text-white hover:bg-primary/90 focus-visible:ring-primary',
        secondary: 'bg-secondary-strong text-white hover:bg-secondary-strong/90 focus-visible:ring-secondary-strong',
        // Amber never carries white (1.75:1). Ink navy on amber is 7.93:1.
        accent: 'bg-accent text-primary hover:bg-accent/90 focus-visible:ring-accent',
        outline: 'border-2 border-primary text-primary hover:bg-primary hover:text-white focus-visible:ring-primary',
        ghost: 'hover:bg-gray-100 hover:text-gray-900 text-gray-700 focus-visible:ring-primary',
        destructive: 'bg-error text-white hover:bg-error/90 focus-visible:ring-error',
        success: 'bg-success text-white hover:bg-success/90 focus-visible:ring-success',
      },
      size: {
        sm: 'px-3 py-1.5 text-sm h-9',
        md: 'px-4 py-2 text-base h-10',
        lg: 'px-6 py-3 text-lg h-12',
        icon: 'h-10 w-10 p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading, disabled, children, ...props }, ref) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading && (
          <svg
            className="h-4 w-4 shrink-0 animate-spin motion-reduce:animate-[spin_1.5s_linear_infinite]"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';

export { Button, buttonVariants };
